import { Voter } from "@activecollab/security";
import moment from "moment";

export class TrackingObjectVoter extends Voter {
  attributes = ["view", "edit", "delete", "reassign", "mark_as_paid", "change_billable_status", "see_billable_status"];

  supports(attribute, subject) {
    if (!this.attributes.includes(attribute)) {
      return false;
    }

    return subject && subject.class === this.trackingObjectClass;
  }

  editInTime(subject, user) {
    const lockTimeAfterDays = this.state.config_options.get(this.lockOptionKey, 30);

    return (
      subject.created_on &&
      subject.user_id === user.id &&
      !moment.unix(subject.created_on).utc().isBefore(moment().utc().subtract(lockTimeAfterDays, "days"))
    );
  }

  isLeaderOrFinancialManager(user, project) {
    return user.custom_permissions.includes("can_manage_finances") || user.id === project.leader_id;
  }

  isInactive(subject) {
    return subject.invoice_item_id || subject.is_trashed;
  }

  isTrackingDisabled(project) {
    return !project || !project.is_tracking_enabled;
  }

  canSeeBillableStatus(subject, user) {
    const project = this.state.projects.get(`${subject.project_id}`);
    if (this.isTrackingDisabled(project)) {
      return false;
    }
    if (project.budget_type === "not_billable") {
      return false;
    }
    if (user.class === "Owner") return true;
    if (!project.members.includes(user.id)) {
      return false;
    }
    if (this.isLeaderOrFinancialManager(user, project)) {
      return true;
    }
    if (user.class === "Member") {
      return project.members_can_change_billable;
    }
    return false;
  }

  canReassign(subject, user) {
    const project = this.state.projects.get(`${subject.project_id}`);
    if (this.isInactive(subject) || this.isTrackingDisabled(project)) {
      return false;
    }
    if (user.class === "Member") {
      if (!project.members.includes(user.id)) {
        return false;
      }
      if (this.isLeaderOrFinancialManager(user, project)) {
        return true;
      }
    }
    return user.class === "Owner";
  }

  voteOnAttribute(attribute, subject, user) {
    switch (attribute) {
      case "view": {
        if (subject.is_trashed) {
          return false;
        }
        const project = this.state.projects.get(`${subject.project_id}`);
        if (this.isTrackingDisabled(project)) {
          return false;
        }
        if (user.class === "Member") {
          if (!project.members.includes(user.id)) {
            return false;
          }
          if (this.isLeaderOrFinancialManager(user, project)) {
            return true;
          }
          return subject.user_id === user.id;
        }
        if (user.class === "Client") {
          if (project.is_client_reporting_enabled) {
            return true;
          }
        }
        return user.class === "Owner";
      }
      case "mark_as_paid": {
        return this.canReassign(subject, user);
      }
      case "reassign": {
        return this.canReassign(subject, user);
      }
      case "delete":
      case "edit": {
        const project = this.state.projects.get(`${subject.project_id}`);
        if (this.isInactive(subject) || this.isTrackingDisabled(project)) {
          return false;
        }
        if (user.class === "Member") {
          if (!project.members.includes(user.id)) {
            return false;
          }
          if (this.isLeaderOrFinancialManager(user, project)) {
            return true;
          }
          if (this.editInTime(subject, user) && subject.billable_status < 2) {
            return true;
          }
        }
        return user.class === "Owner";
      }
      case "see_billable_status": {
        return this.canSeeBillableStatus(subject, user);
      }
      case "change_billable_status": {
        const project = this.state.projects.get(`${subject.project_id}`);
        if (!this.canSeeBillableStatus(subject, user) || this.isTrackingDisabled(project) || this.isInactive(subject)) {
          return false;
        }
        if (user.class === "Member") {
          if (!project.members.includes(user.id)) {
            return false;
          }
          if (this.isLeaderOrFinancialManager(user, project)) {
            return true;
          }
          if (this.editInTime(subject, user) && project.members_can_change_billable && subject.billable_status < 2) {
            return true;
          }
        }
        return user.class === "Owner";
      }
    }
    throw new Error("This code should not be reached!");
  }
}
